import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CertificateClassStep } from '../Certificates/CertificateClassStep';
import {
  CertificateDetailsStep,
  hasValidCertificateDetails,
} from '../Certificates/CertificateDetailsStep';
import { CertificatePreviewStep } from '../Certificates/CertificatePreviewStep';
import {
  CertificateGradesStep,
  haveCompleteCertificateGrades,
} from '../Certificates/CertificateGradesStep';
import { CertificateBatchProgress } from '../Certificates/CertificateBatchProgress';
import { CertificateManualUpload } from '../Certificates/CertificateManualUpload';
import { useCertificateBatch } from '../Certificates/useCertificateBatch';
import { certificateApi } from '../../services/certificateApi';
import './AdminOperations.css';
import './CertificateWizard.css';
import {
  getClases,
  obtenerListadoMatriculasAlumnos,
  subirCertificado,
} from './constants';

const INITIAL_WIZARD_FORM = {
  id_clase: '',
  tipo_emision: 'individual',
  id_alumno: '',
  capacitacion: '',
  fecha_capacitacion: '',
  horas: '',
  fecha_expedicion: '',
};

export const buildCertificatePayload = (
  form,
  recipients = [],
  grades = {},
) => {
  const payload = {
    id_clase: Number(form.id_clase),
    tipo_emision: form.tipo_emision,
    capacitacion: form.capacitacion.trim(),
    fecha_capacitacion: form.fecha_capacitacion.trim(),
    horas: Number(form.horas),
    fecha_expedicion: form.fecha_expedicion,
    calificaciones: recipients.map((student) => ({
      id_alumno: Number(student.id_alumno),
      calificacion: Number(grades[String(student.id_alumno)]),
    })),
  };

  if (form.tipo_emision === 'individual') {
    payload.id_alumno = Number(form.id_alumno);
  }

  return payload;
};

const certificatePayloadKey = (payload) => JSON.stringify(payload);

const studentLabel = (student) =>
  student?.matricula_alumno
    ? `${student.nombre_alumno} (${student.matricula_alumno})`
    : student?.nombre_alumno || '';

export const CrearCertificado = ({
  onPreviewRequest,
  onConfirmPublication,
  onGeneratedArtifactsInvalidated,
}) => {
  const [wizardForm, setWizardForm] = useState(INITIAL_WIZARD_FORM);
  const [gradesByStudent, setGradesByStudent] = useState({});
  const [classes, setClasses] = useState([]);
  const [classStudents, setClassStudents] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [wizardError, setWizardError] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const {
    batch,
    running: batchRunning,
    error: batchError,
    start: startBatch,
    resume: resumeBatch,
    retryFailed: retryFailedBatch,
    clear: clearBatch,
  } = useCertificateBatch();
  const previewRequestId = useRef(0);
  const previewUrlRef = useRef(null);
  const wizardMountedRef = useRef(false);

  const [manualStudents, setManualStudents] = useState([]);
  const [cargandoAlumnos, setCargandoAlumnos] = useState(true);
  const [manualLoadError, setManualLoadError] = useState('');

  const revokePreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };

  const invalidateGeneratedArtifacts = () => {
    previewRequestId.current += 1;
    revokePreviewUrl();
    setPreview(null);
    setPreviewLoading(false);
    setPreviewError('');
    if (typeof onGeneratedArtifactsInvalidated === 'function') {
      onGeneratedArtifactsInvalidated();
    }
  };

  useEffect(() => {
    wizardMountedRef.current = true;

    return () => {
      wizardMountedRef.current = false;
      previewRequestId.current += 1;
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadClasses = async () => {
      try {
        const response = await axios.get(getClases);
        const list = response.data?.data ?? response.data ?? [];
        if (active) {
          setClasses(Array.isArray(list) ? list : []);
        }
      } catch (error) {
        console.error('Error al cargar clases:', error);
        if (active) {
          setClasses([]);
          setWizardError('No fue posible cargar las clases disponibles.');
        }
      } finally {
        if (active) {
          setLoadingClasses(false);
        }
      }
    };

    loadClasses();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    if (!wizardForm.id_clase) {
      setClassStudents([]);
      setLoadingStudents(false);
      return () => {
        active = false;
      };
    }

    setLoadingStudents(true);
    setWizardError('');

    certificateApi
      .listStudents(wizardForm.id_clase)
      .then((students) => {
        if (active) {
          setClassStudents(Array.isArray(students) ? students : []);
        }
      })
      .catch((error) => {
        console.error('Error al cargar alumnos de la clase:', error);
        if (active) {
          setClassStudents([]);
          setWizardError('No fue posible cargar los alumnos de esta clase.');
        }
      })
      .finally(() => {
        if (active) {
          setLoadingStudents(false);
        }
      });

    return () => {
      active = false;
    };
  }, [wizardForm.id_clase]);

  useEffect(() => {
    let active = true;

    const loadManualStudents = async () => {
      try {
        const response = await axios.get(obtenerListadoMatriculasAlumnos);
        const list = response.data?.data?.alumnos ?? [];
        if (active) {
          setManualStudents(Array.isArray(list) ? list : []);
          setManualLoadError('');
        }
      } catch (error) {
        console.error('Error al cargar alumnos:', error);
        if (active) {
          setManualStudents([]);
          setManualLoadError('No fue posible cargar la lista de alumnos.');
        }
      } finally {
        if (active) {
          setCargandoAlumnos(false);
        }
      }
    };

    loadManualStudents();
    return () => {
      active = false;
    };
  }, []);

  const handleClassChange = (idClass) => {
    setWizardForm((current) => ({
      ...current,
      id_clase: idClass,
      id_alumno: '',
    }));
    setClassStudents([]);
    setGradesByStudent({});
    setWizardError('');
    invalidateGeneratedArtifacts();
  };

  const handleModeChange = (mode) => {
    setWizardForm((current) => ({
      ...current,
      tipo_emision: mode,
      id_alumno: '',
    }));
    setGradesByStudent({});
    invalidateGeneratedArtifacts();
  };

  const handleStudentChange = (idStudent) => {
    setWizardForm((current) => ({ ...current, id_alumno: idStudent }));
    setGradesByStudent({});
    invalidateGeneratedArtifacts();
  };

  const handleDetailsChange = (field, value) => {
    setWizardForm((current) => ({ ...current, [field]: value }));
    invalidateGeneratedArtifacts();
  };

  const handleGradeChange = (idStudent, value) => {
    setGradesByStudent((current) => ({
      ...current,
      [String(idStudent)]: value,
    }));
    invalidateGeneratedArtifacts();
  };

  const selectedClassExists = classes.some(
    (classItem) => String(classItem.id_clase) === wizardForm.id_clase,
  );
  const selectedStudentIsEnrolled = classStudents.some(
    (student) => String(student.id_alumno) === wizardForm.id_alumno,
  );
  const recipientsAreValid =
    selectedClassExists &&
    !loadingStudents &&
    classStudents.length > 0 &&
    (wizardForm.tipo_emision === 'masiva' || selectedStudentIsEnrolled);
  const wizardIsValid =
    recipientsAreValid && hasValidCertificateDetails(wizardForm);
  const selectedStudent = classStudents.find(
    (student) => String(student.id_alumno) === wizardForm.id_alumno,
  );
  const recipients =
    wizardForm.tipo_emision === 'masiva'
      ? classStudents
      : selectedStudent
        ? [selectedStudent]
        : [];
  const gradesAreValid = haveCompleteCertificateGrades(
    recipients,
    gradesByStudent,
  );
  const canPreview = wizardIsValid && gradesAreValid;
  const currentPayload = canPreview
    ? buildCertificatePayload(wizardForm, recipients, gradesByStudent)
    : null;
  const currentPayloadKey = currentPayload
    ? certificatePayloadKey(currentPayload)
    : '';
  const previewIsCurrent = Boolean(
    preview &&
      preview.payloadKey === currentPayloadKey &&
      !previewLoading &&
      !previewError,
  );

  const handlePreviewRequest = async (event) => {
    event.preventDefault();
    if (!canPreview) {
      return;
    }

    const payload = buildCertificatePayload(
      wizardForm,
      recipients,
      gradesByStudent,
    );
    if (typeof onPreviewRequest === 'function') {
      onPreviewRequest(payload);
    }

    const requestId = previewRequestId.current + 1;
    previewRequestId.current = requestId;
    revokePreviewUrl();
    setPreview(null);
    setPreviewLoading(true);
    setPreviewError('');

    try {
      const pdf = await certificateApi.preview(payload);
      if (
        !wizardMountedRef.current ||
        requestId !== previewRequestId.current
      ) {
        return;
      }

      const previewUrl = URL.createObjectURL(pdf);
      if (
        !wizardMountedRef.current ||
        requestId !== previewRequestId.current
      ) {
        URL.revokeObjectURL(previewUrl);
        return;
      }

      previewUrlRef.current = previewUrl;
      setPreview({
        url: previewUrl,
        payload,
        payloadKey: certificatePayloadKey(payload),
      });
    } catch {
      if (
        wizardMountedRef.current &&
        requestId === previewRequestId.current
      ) {
        setPreviewError('No fue posible generar la vista previa.');
      }
    } finally {
      if (
        wizardMountedRef.current &&
        requestId === previewRequestId.current
      ) {
        setPreviewLoading(false);
      }
    }
  };

  const handleConfirmPublication = () => {
    if (!previewIsCurrent || batchRunning || batch) {
      return;
    }

    if (typeof onConfirmPublication === 'function') {
      onConfirmPublication(preview.payload);
      return;
    }
    startBatch(preview.payload);
  };

  const handleCreateAnotherBatch = () => {
    clearBatch();
    invalidateGeneratedArtifacts();
    setWizardForm(INITIAL_WIZARD_FORM);
    setGradesByStudent({});
    setClassStudents([]);
    setWizardError('');
  };

  return (
    <div className="admin-operation-page certificate-generation-page">
      <main className="admin-operation-container certificate-wizard-shell">
        <section
          className="certificate-wizard"
          aria-labelledby="certificate-wizard-title"
        >
          <header className="certificate-wizard-header">
            <p className="certificate-wizard-eyebrow">
              Documentación académica
            </p>
            <h1 id="certificate-wizard-title">Generar constancias</h1>
            <p>
              Selecciona una clase, define los destinatarios y captura los datos
              que aparecerán en las constancias.
            </p>
          </header>

          <form
            className="certificate-wizard-form"
            onSubmit={handlePreviewRequest}
            noValidate
          >
            <fieldset
              className="certificate-wizard-controls"
              disabled={batchRunning}
            >
              <CertificateClassStep
                classes={classes}
                students={classStudents}
                idClass={wizardForm.id_clase}
                mode={wizardForm.tipo_emision}
                idStudent={wizardForm.id_alumno}
                onClassChange={handleClassChange}
                onModeChange={handleModeChange}
                onStudentChange={handleStudentChange}
                loadingClasses={loadingClasses}
                loadingStudents={loadingStudents}
              />
              <CertificateDetailsStep
                value={wizardForm}
                onChange={handleDetailsChange}
              />
              <CertificateGradesStep
                students={recipients}
                grades={gradesByStudent}
                onChange={handleGradeChange}
                canPreview={canPreview}
              />
              <CertificatePreviewStep
                previewUrl={preview?.url || ''}
                loading={previewLoading}
                error={previewError}
                mode={wizardForm.tipo_emision}
                selectedStudentLabel={studentLabel(selectedStudent)}
                recipientCount={
                  wizardForm.tipo_emision === 'masiva'
                    ? classStudents.length
                    : selectedStudent
                      ? 1
                      : 0
                }
                canConfirm={previewIsCurrent && !batchRunning && !batch}
                onConfirm={handleConfirmPublication}
              />
            </fieldset>

            <CertificateBatchProgress
              batch={batch}
              running={batchRunning}
              error={batchError}
              onResume={resumeBatch}
              onRetry={retryFailedBatch}
              onClear={handleCreateAnotherBatch}
            />

            {wizardError && (
              <p className="certificate-wizard-status" role="alert">
                {wizardError}
              </p>
            )}
            {!wizardError &&
              wizardForm.id_clase &&
              !loadingStudents &&
              classStudents.length === 0 && (
                <p className="certificate-wizard-status" role="status">
                  Esta clase no tiene alumnos inscritos.
                </p>
              )}
          </form>
        </section>

        <CertificateManualUpload
          students={manualStudents}
          uploadUrl={subirCertificado}
          loadingStudents={cargandoAlumnos}
          loadError={manualLoadError}
        />
      </main>
    </div>
  );
};
